/**
 * AI Model Usage Report (embeddable)
 *
 * Shows token and request usage by user and model. Scope depends on role:
 * - User: own usage only
 * - Tenant Administrator: all users in tenant
 * - Platform Administrator: all tenants (optional tenant filter)
 *
 * Use embedded={true} when rendered inside profile subscription tab.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';

export interface UsageRow {
  usageDate: string;
  userOrDevice: string;
  tenantName: string | null;
  ip: string | null;
  modelId: string | null;
  modelDisplayName: string | null;
  capability: string | null;
  tokensUsed: number;
  requestCount: number;
  costUsd: number;
  tierName?: string | null;
  billingPeriod?: string | null;
  planPriceUsd?: number | null;
}

interface UsageReportResponse {
  rows: UsageRow[];
  summary: { totalTokens: number; totalRequests: number; totalCostUsd: number };
}

const defaultEnd = new Date();
const defaultStart = new Date(defaultEnd.getTime() - 30 * 24 * 60 * 60 * 1000);

function toDateTimeLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

function formatDateAndTime(isoOrLocal: string): string {
  const d = new Date(isoOrLocal.includes('T') ? isoOrLocal : isoOrLocal + 'T00:00:00');
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatDateOnly(dateStr: string): string {
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function displayUserOrDevice(value: string): { text: string; title?: string } {
  if (!value) return { text: '—' };
  if (value.length <= 20 || value.includes('@')) return { text: value };
  return { text: `${value.slice(0, 8)}…${value.slice(-4)}`, title: value };
}

interface TenantOption {
  id: string;
  name: string;
}

export interface ModelUsageReportProps {
  /** When true, compact layout for embedding in profile subscription tab (no page title, minimal outer padding). */
  embedded?: boolean;
}

export const ModelUsageReport: React.FC<ModelUsageReportProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isPlatformAdmin = user?.role === 'Platform Administrator' || user?.role === 'PLATFORM_ADMIN';

  const [startDateTime, setStartDateTime] = useState(() => {
    const s = new Date(defaultStart);
    s.setHours(0, 0, 0, 0);
    return toDateTimeLocal(s);
  });
  const [endDateTime, setEndDateTime] = useState(() => {
    const e = new Date(defaultEnd);
    e.setHours(23, 59, 0, 0);
    return toDateTimeLocal(e);
  });
  const [modelId, setModelId] = useState<string>('');
  const [tenantIdFilter, setTenantIdFilter] = useState<string>('');
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [data, setData] = useState<UsageReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isPlatformAdmin) {
      api.get('/tenants', { params: { limit: 500 } })
        .then((res) => {
          const raw = (res.data as { success?: boolean; data?: unknown })?.success ? (res.data as { data: unknown }).data : res.data;
          const list = Array.isArray(raw) ? raw : [];
          setTenants(list.map((x: { id: string; tenant_name?: string; name?: string }) => ({ id: x.id, name: x.tenant_name || x.name || x.id })));
        })
        .catch(() => setTenants([]));
    }
  }, [isPlatformAdmin]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        startDate: new Date(startDateTime).toISOString(),
        endDate: new Date(endDateTime).toISOString(),
      };
      if (modelId.trim()) params.modelId = modelId.trim();
      if (isPlatformAdmin && tenantIdFilter.trim()) params.tenantId = tenantIdFilter.trim();
      const res = await api.get<UsageReportResponse>('/ai/model-usage', { params });
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDateTime, endDateTime, modelId, tenantIdFilter, isPlatformAdmin]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const content = (
    <>
      <div className="flex flex-wrap gap-4 items-end mb-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-[var(--color-secondary-text)]">
            {t('modelUsage.startDateTime') || 'Start date & time'}
          </span>
          <input
            type="datetime-local"
            value={startDateTime}
            onChange={(e) => setStartDateTime(e.target.value)}
            className="border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-[var(--color-secondary-text)]">
            {t('modelUsage.endDateTime') || 'End date & time'}
          </span>
          <input
            type="datetime-local"
            value={endDateTime}
            onChange={(e) => setEndDateTime(e.target.value)}
            className="border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-[var(--color-secondary-text)]">
            {t('modelUsage.modelOptional') || 'Model (optional)'}
          </span>
          <input
            type="text"
            placeholder={t('modelUsage.modelUuidPlaceholder') || 'Model UUID'}
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            className="border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] w-64"
          />
        </label>
        {isPlatformAdmin && (
          <label className="flex flex-col gap-1">
            <span className="text-sm text-[var(--color-secondary-text)]">
              {t('modelUsage.tenantOptional') || 'Tenant (optional)'}
            </span>
            <select
              value={tenantIdFilter}
              onChange={(e) => setTenantIdFilter(e.target.value)}
              className="border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] min-w-[180px]"
            >
              <option value="">All tenants</option>
              <option value="__anonymous__">Anonymous only</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          onClick={loadReport}
          disabled={loading}
          className="px-4 py-2 rounded bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:opacity-90 disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Apply'}
        </button>
      </div>

      <p className="text-sm text-[var(--color-secondary-text)] mb-4">
        {t('modelUsage.periodLabel') || 'Period:'}{' '}
        <strong className="text-[var(--color-surface-text)]">{formatDateAndTime(startDateTime)}</strong>
        {' – '}
        <strong className="text-[var(--color-surface-text)]">{formatDateAndTime(endDateTime)}</strong>
      </p>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="mb-4 flex flex-wrap gap-6 text-sm">
            <span className="text-[var(--color-secondary-text)]">
              Total tokens:{' '}
              <strong className="text-[var(--color-surface-text)]">
                {data.summary.totalTokens.toLocaleString()}
              </strong>
            </span>
            <span className="text-[var(--color-secondary-text)]">
              Total requests:{' '}
              <strong className="text-[var(--color-surface-text)]">
                {data.summary.totalRequests.toLocaleString()}
              </strong>
            </span>
            <span className="text-[var(--color-secondary-text)]">
              Estimated cost (subscription tokens only):{' '}
              <strong className="text-[var(--color-surface-text)]">
                ${data.summary.totalCostUsd.toFixed(2)}
              </strong>
            </span>
          </div>

          {data.rows.length === 0 ? (
            <p className="text-[var(--color-secondary-text)]">
              {t('modelUsage.noUsage') || 'No usage in this period.'}
            </p>
          ) : (
            <div className="overflow-x-auto border border-[var(--color-border)] rounded">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-secondary-bg)]">
                    <th className="text-left p-2 font-medium text-[var(--color-surface-text)]">
                      {t('modelUsage.table.date') || 'Date'}
                    </th>
                    <th className="text-left p-2 font-medium text-[var(--color-surface-text)]">
                      {t('modelUsage.table.userDevice') || 'User / Device'}
                    </th>
                    <th className="text-left p-2 font-medium text-[var(--color-surface-text)]">
                      {t('modelUsage.table.tenant') || 'Tenant'}
                    </th>
                    <th className="text-left p-2 font-medium text-[var(--color-surface-text)]">
                      {t('modelUsage.table.ip') || 'IP'}
                    </th>
                    <th className="text-left p-2 font-medium text-[var(--color-surface-text)]">
                      {t('modelUsage.table.model') || 'Model'}
                    </th>
                    <th className="text-left p-2 font-medium text-[var(--color-surface-text)]">
                      {t('modelUsage.table.capability') || 'Capability'}
                    </th>
                    <th className="text-right p-2 font-medium text-[var(--color-surface-text)]">
                      {t('modelUsage.table.tokens') || 'Tokens'}
                    </th>
                    <th className="text-right p-2 font-medium text-[var(--color-surface-text)]">
                      {t('modelUsage.table.requests') || 'Requests'}
                    </th>
                    <th className="text-right p-2 font-medium text-[var(--color-surface-text)]">
                      Est. cost (USD)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => {
                    const { text: userOrDeviceText, title: userOrDeviceTitle } = displayUserOrDevice(
                      row.userOrDevice
                    );
                    return (
                      <tr
                        key={`${row.usageDate}-${row.userOrDevice}-${row.modelId ?? 'na'}-${row.capability ?? 'na'}-${i}`}
                        className="border-b border-[var(--color-border)]"
                      >
                        <td className="p-2 text-[var(--color-surface-text)] whitespace-nowrap">
                          {formatDateOnly(row.usageDate)}
                        </td>
                        <td className="p-2 text-[var(--color-surface-text)]" title={userOrDeviceTitle}>
                          {userOrDeviceText}
                        </td>
                        <td className="p-2 text-[var(--color-surface-text)]">{row.tenantName ?? '—'}</td>
                        <td className="p-2 text-[var(--color-surface-text)]">{row.ip ?? '—'}</td>
                        <td className="p-2 text-[var(--color-surface-text)]">{row.modelDisplayName ?? '—'}</td>
                        <td className="p-2 text-[var(--color-surface-text)]">{row.capability ?? '—'}</td>
                        <td className="p-2 text-right text-[var(--color-surface-text)]">
                          {row.tokensUsed.toLocaleString()}
                        </td>
                        <td className="p-2 text-right text-[var(--color-surface-text)]">
                          {row.requestCount.toLocaleString()}
                        </td>
                        <td className="p-2 text-right text-[var(--color-surface-text)]">
                          {row.costUsd > 0 ? `$${row.costUsd.toFixed(4)}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );

  if (embedded) {
    return <div className="mt-2">{content}</div>;
  }

  return (
    <div className="p-4">
      <h1 className="text-xl font-semibold text-[var(--color-surface-text)] mb-4">
        {t('modelUsage.title') || 'AI Model Usage'}
      </h1>
      {content}
    </div>
  );
};
